import { useMemo, useState } from "react";
import { Clock, Save, Pencil, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getApiErrorMessage } from "@/lib/apiClient";
import { useAuth } from "@/hooks/useAuth";

import { ProjetoSearchCombobox } from "@/components/ProjetoSearchCombobox";
import { UsuarioSearchCombobox } from "@/components/UsuarioSearchCombobox";
import { useTarefas } from "@/features/projetos/hooks/useProjetos";
import { useTimesheetFuncionario, useTimesheetMutations } from "@/features/projetos/hooks/useTimesheet";
import { STATUS_TIMESHEET } from "@/features/projetos/types";

const SEM_TAREFA = "0";
const isoLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtData = (d: string | null) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "—");

function semanaAtual(): [string, string] {
  const hoje = new Date();
  const offset = (hoje.getDay() + 6) % 7; // segunda = 0
  const seg = new Date(hoje); seg.setDate(hoje.getDate() - offset);
  const dom = new Date(seg); dom.setDate(seg.getDate() + 6);
  return [isoLocal(seg), isoLocal(dom)];
}

export default function ApontamentoHoras() {
  const { user } = useAuth();
  const [s0, s1] = useMemo(semanaAtual, []);
  const [funcionarioId, setFuncionarioId] = useState(user?.id ?? 0);
  const [inicio, setInicio] = useState(s0);
  const [fim, setFim] = useState(s1);

  const { data: lancamentos = [] } = useTimesheetFuncionario(funcionarioId, inicio, fim);
  const { criar, atualizar, excluir } = useTimesheetMutations();

  // editor
  const [editId, setEditId] = useState<number | null>(null);
  const [projetoId, setProjetoId] = useState("");
  const [tarefaId, setTarefaId] = useState(SEM_TAREFA);
  const [data, setData] = useState(s0);
  const [horas, setHoras] = useState("0");
  const [descricao, setDescricao] = useState("");
  const [faturavel, setFaturavel] = useState(false);

  const { data: tarefas = [] } = useTarefas(projetoId ? Number(projetoId) : undefined);

  const limpar = () => {
    setEditId(null); setProjetoId(""); setTarefaId(SEM_TAREFA); setData(inicio);
    setHoras("0"); setDescricao(""); setFaturavel(false);
  };

  const moverSemana = (dias: number) => {
    const ni = new Date(inicio); ni.setDate(ni.getDate() + dias);
    const nf = new Date(fim); nf.setDate(nf.getDate() + dias);
    setInicio(isoLocal(ni)); setFim(isoLocal(nf));
  };

  const salvar = async () => {
    if (!projetoId) { toast.error("Selecione o projeto."); return; }
    if (Number(horas) <= 0) { toast.error("Informe as horas (> 0)."); return; }
    const req = {
      projetoId: Number(projetoId), funcionarioId, dtApontamento: data,
      tarefaId: Number(tarefaId), horas: Number(horas), descricao, faturavel,
    };
    try {
      if (editId) await atualizar.mutateAsync({ id: editId, req });
      else await criar.mutateAsync(req);
      toast.success("Apontamento salvo.");
      limpar();
    } catch (e) { toast.error(getApiErrorMessage(e, "Erro ao salvar apontamento.")); }
  };

  const remover = async (id: number) => {
    try { await excluir.mutateAsync(id); toast.success("Apontamento removido."); if (editId === id) limpar(); }
    catch (e) { toast.error(getApiErrorMessage(e, "Erro ao remover apontamento.")); }
  };

  const totalHoras = lancamentos.reduce((s, l) => s + Number(l.horas), 0);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold"><Clock className="h-6 w-6" /> Apontamento de Horas</h1>
        <p className="text-sm text-muted-foreground">Registre as horas trabalhadas por projeto e tarefa.</p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <div className="w-[260px]">
            <Label>Funcionário</Label>
            <UsuarioSearchCombobox value={funcionarioId} onChange={id => setFuncionarioId(id)} placeholder="Selecione o funcionário..." />
          </div>
          <div><Label>Início</Label><Input type="date" value={inicio} onChange={e => setInicio(e.target.value)} /></div>
          <div><Label>Fim</Label><Input type="date" value={fim} onChange={e => setFim(e.target.value)} /></div>
          <Button variant="outline" size="icon" onClick={() => moverSemana(-7)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" onClick={() => moverSemana(7)}><ChevronRight className="h-4 w-4" /></Button>
          <div className="ml-auto text-sm text-muted-foreground">Total no período: <span className="font-semibold tabular-nums">{totalHoras.toFixed(2)} h</span></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">{editId ? "Editar apontamento" : "Novo apontamento"}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <Label>Projeto</Label>
              <ProjetoSearchCombobox
                somenteAtivos
                value={projetoId ? Number(projetoId) : undefined}
                onChange={id => { setProjetoId(id ? String(id) : ""); setTarefaId(SEM_TAREFA); }}
              />
            </div>
            <div>
              <Label>Tarefa</Label>
              <Select value={tarefaId} onValueChange={setTarefaId} disabled={!projetoId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEM_TAREFA}>(sem tarefa)</SelectItem>
                  {tarefas.map(t => <SelectItem key={t.id} value={String(t.id)}>{t.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Data</Label><Input type="date" value={data} onChange={e => setData(e.target.value)} /></div>
            <div><Label>Horas</Label><Input type="number" step="0.25" value={horas} onChange={e => setHoras(e.target.value)} /></div>
            <div className="md:col-span-2"><Label>Descrição</Label><Input value={descricao} onChange={e => setDescricao(e.target.value)} maxLength={300} /></div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2"><Switch checked={faturavel} onCheckedChange={setFaturavel} /><Label>Faturável</Label></div>
            <Button onClick={salvar}><Save className="mr-2 h-4 w-4" />{editId ? "Atualizar" : "Adicionar"}</Button>
            {editId && <Button variant="outline" onClick={limpar}>Cancelar</Button>}
          </div>

          <Table>
            <TableHeader><TableRow>
              <TableHead>Data</TableHead><TableHead>Projeto</TableHead><TableHead>Tarefa</TableHead><TableHead>Horas</TableHead>
              <TableHead>Faturável</TableHead><TableHead>Descrição</TableHead><TableHead>Status</TableHead>
              <TableHead className="w-24 text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {lancamentos.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">Nenhum apontamento no período.</TableCell></TableRow>
              ) : lancamentos.map(l => (
                <TableRow key={l.id}>
                  <TableCell>{fmtData(l.dtApontamento)}</TableCell>
                  <TableCell>{l.projetoNome ?? "—"}</TableCell>
                  <TableCell>{l.tarefaNome ?? <span className="text-muted-foreground">(sem tarefa)</span>}</TableCell>
                  <TableCell className="tabular-nums">{Number(l.horas).toFixed(2)}</TableCell>
                  <TableCell>{l.faturavel ? "Sim" : "Não"}</TableCell>
                  <TableCell className="max-w-[240px] truncate">{l.descricao}</TableCell>
                  <TableCell><Badge variant="outline">{STATUS_TIMESHEET[l.status] ?? l.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" disabled={l.status !== 0}
                      onClick={() => { setEditId(l.id); setProjetoId(String(l.projetoId)); setTarefaId(String(l.tarefaId)); setData(l.dtApontamento.slice(0, 10)); setHoras(String(l.horas)); setDescricao(l.descricao ?? ""); setFaturavel(l.faturavel); }}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" disabled={l.status === 1} onClick={() => remover(l.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
